import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CalendarClock, Loader2, MessageSquareText, Save } from "lucide-react";

export const MONTHS = ["", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

const PACE_MIN = 0.8;
const pace = (r) => {
  const p = num(r.planned), g = num(r.given);
  if (!r.ended || !p) return null;
  if (g === null) return { level: "missing", text: "Aulas dadas não informadas" };
  return g < p * PACE_MIN ? { level: "low", text: `${p - g} aula(s) abaixo do previsto (${Math.round((g / p) * 100)}%)` } : null;
};

const PaceAlert = ({ rows }) => {
  const low = rows.filter((r) => pace(r)?.level === "low");
  const missing = rows.filter((r) => pace(r)?.level === "missing");
  if (!low.length && !missing.length) return null;
  return (
    <div className={`mb-3 flex gap-2 rounded-lg border p-3 text-sm ${low.length ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="pace-alert">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>
        {low.length > 0 && <p data-testid="pace-alert-low"><b>Ritmo abaixo do previsto:</b> {low.map((r) => `${MONTHS[r.month]} (${r.given} de ${r.planned})`).join(", ")}. Planeje a reposição dessas aulas.</p>}
        {missing.length > 0 && <p data-testid="pace-alert-missing"><b>Aulas dadas sem preencher:</b> {missing.map((r) => MONTHS[r.month]).join(", ")}.</p>}
      </div>
    </div>
  );
};

export const PlannedLessonsCard = ({ classId, subjectId, term, closed, reloadKey }) => {
  const [rows, setRows] = useState(null);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [hasCal, setHasCal] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/diary/planned", { params: { class_id: classId, subject_id: subjectId, term_order: term } })
      .then((r) => { setRows(r.data.months); setHasSchedule(r.data.has_schedule); setHasCal(r.data.has_calendar); }).catch(() => setRows([]));
  }, [classId, subjectId, term]);
  useEffect(() => { load(); }, [load, reloadKey]);
  if (!rows) return null;
  const set = (i, k, v) => setRows((x) => x.map((y, j) => (j === i ? { ...y, [k]: v } : y)));
  const pack = (k) => Object.fromEntries(rows.filter((r) => num(r[k]) !== null).map((r) => [String(r.month), num(r[k])]));
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/diary/planned", { class_id: classId, subject_id: subjectId, term_order: term, months: pack("planned"), given: pack("given"),
        notes: Object.fromEntries(rows.map((r) => [String(r.month), r.note || ""])) });
      toast.success("Aulas previstas, dadas e observações salvas"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const suggest = () => {
    setRows((x) => x.map((r) => ({ ...r, planned: r.suggested ?? r.planned })));
    toast.info(hasCal ? "Sugestão aplicada (horário + calendário escolar). Confira e clique em Salvar." : "Sugestão aplicada pelo horário (sem calendário escolar cadastrado). Confira e clique em Salvar.");
  };
  const given = (r) => num(r.given) || 0;
  const tp = rows.reduce((s, r) => s + (num(r.planned) || 0), 0);
  const tg = rows.reduce((s, r) => s + given(r), 0);
  return (
    <Card className="mb-4 border-slate-200 p-4" data-testid="planned-lessons-card">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-display text-sm font-bold uppercase tracking-wide text-slate-700">Aulas previstas e dadas</h3>
        <div className="flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" onClick={suggest} disabled={closed || !hasSchedule || !rows.length} title={hasSchedule ? "Calcula pelos tempos da disciplina no quadro de horários, descontando feriados e dias sem aula do calendário escolar" : "Monte o quadro de horários da turma para usar a sugestão"} data-testid="planned-suggest">
          <CalendarClock className="mr-1.5 h-4 w-4" />Recalcular previstas
        </Button>
        <Button size="sm" onClick={save} disabled={busy || closed || !rows.length} className="bg-blue-700 hover:bg-blue-800" data-testid="planned-save">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Salvar
        </Button>
        </div>
      </div>
      {rows.length > 0 && <PaceAlert rows={rows} />}
      {!rows.length ? <p className="text-sm text-slate-400">Cadastre as datas do trimestre no ano letivo para listar os meses.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[320px] text-sm">
            <thead><tr className="border-b text-left text-xs font-semibold uppercase text-slate-500"><th className="py-1.5">Mês de</th><th className="py-1.5 text-center">Aulas previstas</th><th className="py-1.5 text-center">Aulas dadas</th></tr></thead>
            <tbody>
              {rows.map((r, i) => { const pc = pace(r); return (
                <tr key={r.month} className={`border-b border-slate-100 ${pc?.level === "low" ? "bg-red-50/60" : ""}`} data-testid={`planned-row-${r.month}`}>
                  <td className="py-1.5 font-medium text-slate-700">{MONTHS[r.month]}
                    {pc && <span className={`ml-1.5 inline-flex items-center gap-0.5 text-[11px] font-semibold ${pc.level === "low" ? "text-red-600" : "text-amber-600"}`} title={pc.text} data-testid={`pace-flag-${r.month}`}><AlertTriangle className="h-3 w-3" />{pc.level === "low" ? "abaixo" : "pendente"}</span>}
                  </td>
                  <td className="py-1.5"><Input type="number" min="0" disabled={closed} value={r.planned ?? ""} placeholder={r.suggested != null ? String(r.suggested) : ""} onChange={(e) => set(i, "planned", e.target.value)} className="mx-auto h-8 w-20 text-center" data-testid={`planned-month-${r.month}`} /></td>
                  <td className="py-1.5"><Input type="number" min="0" disabled={closed} value={r.given ?? ""} placeholder="—" onChange={(e) => set(i, "given", e.target.value)} className="mx-auto h-8 w-20 text-center" data-testid={`given-month-${r.month}`} /></td>
                </tr>
              ); })}
              <tr className="font-bold text-slate-800"><td className="py-1.5">TOTAL</td><td className="text-center" data-testid="planned-total">{tp}</td><td className="text-center" data-testid="given-total">{tg}</td></tr>
            </tbody>
          </table>
          <p className="mt-2 text-xs text-slate-400">Aulas previstas: calculadas pelo quadro de horários descontando feriados e dias sem aula do calendário escolar{hasCal ? "" : " (calendário ainda não enviado)"} — clique no campo para alterar. Aulas dadas: preenchidas pelo professor, coordenação ou direção. Alerta de ritmo: mês encerrado com menos de 80% das aulas previstas.</p>
          <div className="mt-4 border-t border-slate-100 pt-3" data-testid="month-notes">
            <h4 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600"><MessageSquareText className="h-4 w-4" />Observações do mês (saem no PDF)</h4>
            <div className="grid gap-3 md:grid-cols-2">
              {rows.map((r, i) => (
                <div key={r.month}>
                  <label className="mb-1 flex justify-between text-xs font-semibold text-slate-600"><span>{MONTHS[r.month]}</span><span className="font-normal text-slate-400">{(r.note || "").length}/600</span></label>
                  <Textarea rows={2} maxLength={600} disabled={closed} value={r.note || ""} onChange={(e) => set(i, "note", e.target.value)}
                    placeholder="Ex.: Turma participativa; reposição da aula do dia 21 marcada." className="text-sm" data-testid={`month-note-${r.month}`} />
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-slate-400">Clique em Salvar para gravar. No PDF o texto sai em letras maiúsculas.</p>
          </div>
        </div>
      )}
    </Card>
  );
};
